import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

/* -------------------
   Global fonts
   ------------------- */
   const GlobalStyles = createGlobalStyle`
   /* Reset & box-sizing */
   *, *::before, *::after {
     box-sizing: border-box;
   }
   html, body {
     margin: 0;
     padding: 0;
   }
 
   /* Font faces */
   @font-face {
     font-family: 'AnybodyMedium';
     src: url('../assets/fonts/Anybody-Medium.ttf') format('truetype');
     font-weight: 500;
     font-style: normal;
     font-display: swap;
   }
 
   @font-face {
     font-family: 'AnybodyBlack';
     src: url('../assets/fonts/Anybody-Black.ttf') format('truetype');
     font-weight: 900;
     font-style: normal;
     font-display: swap;
   }
 `;
 

/* -------------------
   Breakpoints
   ------------------- */
const breakpoints = {
  mobile: '40rem',
  tablet: '64rem',
};

/* -------------------
   Base layout
   ------------------- */
const Page = styled.article`
  max-width: 110rem;
  margin: 0 auto;
  background: #fff;
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
  color: #000;
  overflow-x: hidden;
  padding: 0 1.5rem;
`;

/* -------------------
   Hero (full‑bleed)
   ------------------- */
const Hero = styled.section`
  position: relative;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
`;

const HeroImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const HeroTitle = styled.h1`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #fff;
  font-family: 'AnybodyBlack', 'Anybody', sans-serif;
  font-weight: 900;
  font-size: clamp(3rem, 8vw, 9.375rem);
  line-height: 1;
  padding: 0 1rem;
  pointer-events: none;
`;

/* -------------------
   Text styles
   ------------------- */
const Lead = styled.p`
  font-weight: 700;
  font-size: clamp(1.125rem, 2vw, 1.75rem);
  line-height: 1.4;
  max-width: 80ch;
  margin: 2rem auto;
  text-align: center;
`;

const TrackSection = styled.section`
  margin: 4rem 0;
  text-align: center;
  max-width: ${({ $wide, $narrow }) =>
    $wide ? '110rem' :
    $narrow ? '50rem' : '80ch'};
  padding: 0 8rem;

  /* alternating alignment for narrow sections */
  ${({ $narrow, $alignRight }) =>
    $narrow && `
      margin-left: ${$alignRight ? 'auto' : '0'};
      margin-right: ${$alignRight ? '0' : 'auto'};
    `
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0 0.5rem;
    /* сохраняем горизонтальные отступы от выравнивания выше */
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  @media (min-width: ${breakpoints.tablet}) {
    margin-top: 6rem;
    margin-bottom: 6rem;
  }
`;

const TrackTitle = styled.h2`
  font-weight: 700;
  font-size: clamp(1.125rem, 2vw, 1.75rem);
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const TrackDescription = styled.p`
  font-weight: 400;
  font-size: clamp(1rem, 1.8vw, 1.75rem);
  line-height: 1.4;
`;

const SpotifyFrame = styled.iframe.attrs({
  frameBorder: '0',
  allowtransparency: 'true',
})`
  display: block;
  border-radius: 0.75rem;
  width: 100%;
  /* ~152px = 9.5rem */
  height: 15rem;
  margin: 2rem auto 0;

  @media (max-width: ${breakpoints.mobile}) {
    /* ~80px = 5rem */
    height: 10rem;
    margin: 1.5rem auto 0;
    max-width: 95%;
  }
`;



const SpecialHeading = styled.h3`
  font-weight: 700;
  font-size: clamp(1.125rem, 2vw, 1.75rem);
  text-align: center;
  margin: 4rem auto 2rem;
  max-width: 60ch;
`;

/* -------------------
   Footer (full‑bleed)
   ------------------- */
   const FooterBar = styled.footer`
   position: relative;
   width: 100vw;                     /* растягиваем на весь вьюпорт  */
   margin-left: calc(-50vw + 50%);   /* «выравниваем» к краю окна   */
 
   background: #000;
   padding: clamp(3rem, 1vh, 3rem) 0;
   text-align: center;
 
   h1 {
     font-family: 'AnybodyBlack', 'Anybody', sans-serif;
     color: #fff;
     font-size: clamp(2.6rem, 8vw, 8rem);
     font-weight: 900;
     margin: 0;
     line-height: 1;
   }
 `;
 
/* ===============================================================
   Component
   =============================================================== */

export default function FashionWeek() {
  return (
    <Page>
      <GlobalStyles />

      {/* Hero */}
      <Hero>
        <HeroImg src="/assets/fashionweek.png" alt="London Fashion Week runway" />
        <HeroTitle>THE SOUNDS OF LFW AW25</HeroTitle>
      </Hero>

      {/* Intro */}
      <Lead>
        Though February has been and gone, the songs we heard throughout the shows have become such earworms we just had
        to get them out and into writing to move on!<br />
        Gabriel Mealor-Pritchard had Shazam on standby and now indulges in his top soundtracks of LFW AW25.
      </Lead>

      {/* Tracks */}
      {tracks.map(({ title, description, spotify }, idx) => (
        <React.Fragment key={title}>
          {idx === 10 && (
            <SpecialHeading>
              The following three tunes are special mentions – those certain shows with a custom soundtrack you just can’t
              find elsewhere.
            </SpecialHeading>
          )}

          <TrackSection
            $wide={idx < 10}
            $narrow={idx >= 10}
            $alignRight={idx === 11}
          >
            <TrackTitle>{title}</TrackTitle>
            <TrackDescription dangerouslySetInnerHTML={{ __html: description.replace(/\n/g, '<br/>') }} />
            {spotify && (
              <SpotifyFrame
                src={`https://open.spotify.com/embed/track/${spotify}?utm_source=generator`}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              />
            )}
          </TrackSection>
        </React.Fragment>
      ))}

      {/* Footer */}
      <FooterBar>
        <h1>RIFF MAGAZINE</h1>
      </FooterBar>
    </Page>
  );
}

/* -------------------
   Data
   ------------------- */
const tracks = [
  {
    title: `'Ray of Light' by Madonna – Naomi Hart London (Breakthrough)`,
    description: `The brand's debut show launched via a soundtrack of vintage, melancholic jazz, built to a crescendo, seamlessly blending into Madonna’s iconic 'Ray of Light' for its finale.
After the show, Hart told us, “The music at the start was supposed to be eerie, old-fashioned, but also to present the idea of fantasy as if daydreaming,” adding, “though the main idea behind the clothes is the girls feeling strong and feeling powerful.” Illustrating Hart’s desire to unite the garments presented with the music chosen,  this was a beautifully curated presentation ending with the strong female voice of Pop’s de facto matriarch.`,
    spotify: `4Fpsa3pYc3gozrxHRRPvR6`,
  },
  {
    title: `'What's a Girl to Do' by Bat for Lashes – Tifaret (Fashion Scout)`,
    description: `"A beautiful depression," mused Chiuye, Creative Director of Tifaret Collection, as she spoke to us of the music she chose for her breakthrough show at London Fashion Week.  "I feel like I'm in a gloomy and depressive feeling," she adds, with the sombre soundtrack reflecting a romantic side to the collection through the fluidity in both fabric and sound.`,
    spotify: `5LIRCoKAH9h2N5ViqjQDIJ`,
  },
  {
    title: `'West End Girls' by Pet Shop Boys – S.S.Daley`,
    description: `In a flurry of floral jacquard bomber jackets and cropped duffles, Steven Stokey-Daley touched on the clichés of British culture before setting off his models to the beat of the sensational gay club tune, West End Girls, a staple in British music. In a West End town, a dead-end world, S.S.Daley brings us turned-up sleeves and a passion for fashion, splurting out to Vogue at his studio that "It's unmessed-about wardrobe bangers! That's what I want to do, really."`,
    spotify:'2Di0qFNb7ATroCGB3q0Ka7',
  },
  {
    title: `'Echoes of Dust' by Ramoon – Maison ARTC`,
    description: `What better way to kick off a celebration of Moroccan culture at London Fashion Week's 'Kingdom of Light' collaborative show with the country's tourism board than to ignite the stereos with North African music, blended with a pulsating, percussive finish? Ramoon, a close friend of Maison ARTC, collaborated with the designer to produce a series of tracks for the looks. The tune's allegro tempo and staccato rhythms found themselves married to the series of colours and textures presented throughout the collection. "Music for me, it's passion, and I want the music and clothes to create something that's not another thing that we have seen already," ARTC told us in an interview shortly after his show, adding, "So it was very important that we created something very happy, like Morocco.`,
    spotify: '1YxqCYYkfGuFRC7JftjXWj',
  },
  {
    title: `'Wildflower' by Billie Eilish – Pauline Dujancourt`,
    description: `The sheer beauty of Pauline Dujancourt's latest collection resonated with the melodies of 'Wildflower' by Billie Eilish, with miniature crochet flowers as motifs that link the two with ease. Dujancourt recounted her childhood when she spent time with her French grandmother, "She had a plant with red flowers that bloomed once a year, on her birthday in February," she told Vogue Runway. Paying homage to the martiarch through her designs. Wildflower, a song planted in love and growth, though in different terms, seemed fitting to accompany her collection down the catwalk.`,
    spotify: '3QaPy1KgI7nu9FJEQUgn6h',
  },
  {
    title: `'Always on My Mind' by The Pet Shop Boys – Richard Quinn`,
    description: `Using a classic pop track can be a bit of a gamble, but Quinn’s choice of the Boys’ version of the Elvis hit was the perfect choice to accompany the exquisite show the brand held.
Celebrating London's influence on his designs, Quinn's AW25 presentation was radiating and elevating, marking the 80s pop classic the perfect fit. Not to mention that this song was the UK's Christmas number one song in the charts in 1987... now that Winter Wonderland makes sense!`,
    spotify: `07ABETRdek3ACMpRPvQuaT`,
  },
  {
    title: `I Want Your (Hands On Me) by Sinéad O’Connor – Burberry`,
    description: `Taken from O'Connor's 1987 album 'The Lion And The Cobra,' Lee paid homage to the singer by using most of her iconic album as the foundation for the soundtrack of his final show for the house. 
Also included in the medley were: 'Troy', 'I Am Stretched On Your Grave', 'Just Like U Said It Would B', & 'Special Cases' by Massive Attack ft. Sinéad O’Connor. A plethora of iconic music which effortlessly exemplified Daniel Lee's term as Creative Director. `,
    spotify: `3FwJ6vxxKhhvet3DM042p9`,
  },
  {
    title: `Kaye Donachie's cover of 'Love Will Tear Us Apart' – Erdem`,
    description: `This hauntingly beautiful take on the iconic post-punk tune echoed around the British Museum as the finale of Erdem's AW25 runway took place. The stripped-back vocals went hand in hand with the raw beauty of the pieces presented, staying remarkably in tune with the tonality of originality that Erdem never fails to provide.`,
    spotify: `2JO3HwMRPeya8bXbtbyPcf`,
  },
  {
    title: `'No One Gets the Prize' by Diana Ross – Conner Ives`,
    description: `Ives brought energy and edge to the stage of his collection release with the use of this disco-soul gem by Diana Ross. Showcasing his unapologetically bold designs to the masses, the accompanying anthemic track channelled power and purpose. The designer appeared at the end of the show, donning a simple white T with "PROTECT THE DOLLS" emblazoned across the chest. The decision to choose Diana Ross — known to many as the singer of a go-to LGBTQ+ club-pop anthem — was perfect. Ross's voice consolidated Ives' vision, literally to a T.`,

    spotify: `3z8EX1QoPW20ME8IueIADZ`,
  },
  {
    title: `'Adagio for Strings Op. 11' by Samuel Barber – Maison Sara Chraïbi`,
    description: `And to finish, a beautiful needle drop of this classical masterpiece, Adagio for Strings. Written in 1936, the tune stands strong amid the modern day, its timeless elegance complementing the heavenly pieces showcased at Maison Sara Chrïibi's London Fashion Week appearance. She told RIFF Magazine, "it's about this relation between the earthly and the heavenly skies, poetic material," and what better a song to choose from to carry the collection, lifting it into those heavenly skies.`,
    spotify: `1CSaCKPIp2yCIDL3t7Fyau`,
  },
  {
    title: `'Unknown Title' by Thomas Broda – Khanh Brice Nguyen`,
    description: `Commissioned by Khanh Brice Nguyen, drummer and composer Thomas Broda was presented with the task of creating an inspiring and ethereal track to accompany Nguyen’s mesmerising show at St John's Church, Hyde Park. 

Not only did Broda have to consider the movement of Nguyen’s designs within the process of his creation, but also the unexpected element of interpretive dance that shocked the onlookers, ending the show with an unmistakable beauty. The music told a story, and what more could you want than that?`,
    spotify: null,
  },
  {
    title: `'Angel of Death' by Kid Brunswick – Jean Louie Castillo`,
    description: `Created specifically for the show by the artist formerly known as Kid Brunswick,the heavy metal, goth rock track that resonated around the Banking Hall, City of London. 
During our interview, Brunswick was keen to point out his intentions for the soundtrack: "I wanted to create something that didn’t just accompany the show, but amplified its individuality—something that felt as bold and unconventional as Jean’s designs." And that's exactly what he did: a multi-sensory soundscape encompassing a unique presentation of avant-garde designs that wowed each and every person in the room.`,
    spotify: null,
  },
  {
    title: `'Unknown Title' by Holden Federico – Leo Prothmann`,
    description: `"Music has always been a close companion of mine, and ever since I presented my first show, 'FW23 disciplines,' I have collaborated with Holden Federico, a New York-based DJ and musician.”Designer Leo Prothmann enthused about his ongoing work with the DJ, who is well known for blending elements of soul and grit throughout his pieces. The pair have collaborated once more on Prothmann's recent AW25 show - treating us to a bespoke playlist featuring Federico’s hallmark sound.`,
    spotify: null,
  },
];
